import { inject, injectable } from 'inversify';
import type { HttpClientInterface } from '../http/httpClientInterface';
import type {
  SymfonyResponse,
  SendOtpData,
  VerifyOtpData
} from './types';
import { ApiException } from '../errors/ApiException';
import type { OtpClientInterface } from './otpClientInterface';

@injectable()
export class OtpClient implements OtpClientInterface {
  constructor (
    @inject('OtpHttpClient')
    private httpClient: HttpClientInterface
  ) {}

  /* -------------------------
     SEND OTP
  -------------------------- */
  public async send (
    phoneNumber: string,
    purpose?: string,
    cartId?: string,
    from?: string
  ): Promise<SendOtpData> {
    const otpData = {
      phone_number: phoneNumber,
      purpose,
      cart_id: cartId,
      sender_from: from || 'Nexee'
    };
    const response = await this.httpClient.post<
      SymfonyResponse<SendOtpData>
    >('send', { json: otpData });

    return this.unwrap(response);
  }

  /* -------------------------
     VERIFY OTP
  -------------------------- */
  public async verify (
    phoneNumber: string,
    code: string,
    purpose?: string
  ): Promise<VerifyOtpData> {
    const verifyData = {
      phone_number: phoneNumber,
      code,
      purpose
    };
    const response = await this.httpClient.post<
      SymfonyResponse<VerifyOtpData>
    >('verify', { json: verifyData });

    return this.unwrap(response);
  }

  /* -------------------------
     RESEND OTP
  -------------------------- */
  public async resend (
    phoneNumber: string,
    purpose?: string
  ): Promise<SendOtpData> {
    return this.send(phoneNumber, purpose);
  }

  /* -------------------------
     INTERNAL RESPONSE UNWRAP
  -------------------------- */
  private unwrap<T> (response: SymfonyResponse<T>): T {
    if (!response.success) {
      throw new ApiException({
        message: response.error?.message ?? 'OTP request failed',
        code: response.error?.code,
        retryable: response.error?.retryable,
        requiresNewCode: response.error?.requires_new_code,
        details: response.error?.details,
        status: 'error'
      });
    }

    if (!response.data) {
      throw new ApiException({
        message: 'Missing OTP response data'
      });
    }

    return response.data;
  }
}
